/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import orderBy from "lodash/orderBy";

import { DEFAULT_PAGE_WIDTH } from "./constants";
import { Coordinates, Size } from "./entities/block.entity";

const blockXIsLegal = (newCoordinates: Coordinates, coordinatesOnPage: Coordinates[]): boolean => {
  return coordinatesOnPage.every(({ top, left, width, height }) => {
    // prev block completely to left or completely to right
    const xCoordsOk = left + width < newCoordinates.left || newCoordinates.left + newCoordinates.width < left;
    const yCoordsOk = top + height < newCoordinates.top || newCoordinates.top + newCoordinates.height < top;
    return (
      (xCoordsOk || yCoordsOk) &&
      newCoordinates.left + newCoordinates.width <= DEFAULT_PAGE_WIDTH &&
      newCoordinates.top >= 0 &&
      newCoordinates.left >= 0
    );
  });
};

/**
 * Gets the coordinates of the best place to put the embedded block on the connected page
 * Gets the highest then the leftmost point where the block can be legally
 *
 * The key insight is that the best position will be somewhere near a corner
 * OR at top / left of board (along border)
 *
 * @param existingPageBlocks - the existing blocks for the page being embedded onto
 * @param embeddedBlockCoordinates - the coordinates of the embedded block
 * @param padding - minimum padding needed
 */
interface Props {
  positionedBlocks: Coordinates[];
  naiveCoordinates: Coordinates;
  padding?: number;
  goLeft?: boolean;
}
export function getBestSpotForBlock({
  positionedBlocks,
  naiveCoordinates,
  padding = 10,
  goLeft = true,
}: Props): Coordinates {
  // Step 1: Find potential positions to put the new block by looking at the corners of other blocks, either the highest and leftmost or highest and rightmost
  const possibleTopLeftCorners = goLeft
    ? positionedBlocks
        .map(({ height, width, left, top }) => [
          // for leftmost
          { left: left + width + padding, top },
          { left, top: top + height + padding },
        ])
        .flat()
        .map((c) => ({
          ...c,
          width: naiveCoordinates.width,
          height: naiveCoordinates.height,
        }))
    : positionedBlocks
        .map(({ height, width, left, top }) => [
          // for rightmost
          { left: left - naiveCoordinates.width - padding, top },
          { left: left + width - naiveCoordinates.width, top: top + height + padding },
        ])
        .flat()
        .map((c) => ({
          ...c,
          width: naiveCoordinates.width,
          height: naiveCoordinates.height,
        }));

  // curry in the existing page blocks to make legality a function just of corner
  const curriedCornerIsLegal = (possibleCorner: Coordinates) =>
    blockXIsLegal(
      { ...possibleCorner, height: possibleCorner.height },
      positionedBlocks.map(({ top, left, width, height }) => ({
        top,
        left,
        width,
        // add in height of page box if existing block is an embedded block
        height,
      }))
    );

  // Step 2: Figure out which corners are legal
  const legalCorners = possibleTopLeftCorners.filter(curriedCornerIsLegal);

  // Step 3: Try moving each up and left to border
  const borderCorners = possibleTopLeftCorners
    .map(({ top, left, width, height }) => [
      { top: 0, left, width, height },
      { top, left: 0, width, height },
      { top, left: DEFAULT_PAGE_WIDTH - naiveCoordinates.width, width, height },
    ])
    .flat()
    .filter(curriedCornerIsLegal);

  // Step 3: Return the best of the legal corners
  const preferredCorners = orderBy(
    [...legalCorners, ...borderCorners],
    ["top", "left"],
    ["asc", goLeft ? "asc" : "desc"]
  );
  // Step 4: Find best corner, or if none return (0,0) position (needed in new page which has no block corners)
  let bestBlock = preferredCorners[0];

  // Step 5: Try to move the block up and left/right as far as possible
  if (bestBlock) {
    while (blockXIsLegal(bestBlock, positionedBlocks)) {
      bestBlock.left += goLeft ? -10 : 10;
    }
    bestBlock.left += goLeft ? 10 : -10;

    // Check if we can move the block to either side of the page
    bestBlock.left = blockXIsLegal({ ...bestBlock, left: DEFAULT_PAGE_WIDTH - bestBlock.width }, positionedBlocks)
      ? DEFAULT_PAGE_WIDTH - bestBlock.width
      : bestBlock.left;
    bestBlock.left = blockXIsLegal({ ...bestBlock, left: 0 }, positionedBlocks) ? 0 : bestBlock.left;

    while (blockXIsLegal(bestBlock, positionedBlocks)) {
      bestBlock.top -= 10;
    }
    bestBlock.top += 10;
  }

  return (
    preferredCorners[0] || {
      ...naiveCoordinates,
      top: 0,
      left: goLeft ? 0 : DEFAULT_PAGE_WIDTH - naiveCoordinates.width,
    }
  );
}

export class BlockPlacer {
  private static readonly ONE_GRID_UNIT = 10; // distance between grid dots

  private static readonly BUFFER = 10; // distance between blocks

  static isOverlapping(blockA: Coordinates, blockB: Coordinates, buffer: number): boolean {
    return (
      blockA.left < blockB.left + blockB.width + buffer &&
      blockA.left + blockA.width > blockB.left + buffer &&
      blockA.top < blockB.top + blockB.height + buffer &&
      blockA.top + blockA.height > blockB.top + buffer
    );
  }

  /**
   * Finds an available position for a new block without overlapping existing blocks.
   *
   * @param {number} plateHeight - The height of the plate on which the blocks are placed.
   * @param {Size} newBlock - The size of the new block to be placed.
   * @param {Coordinates[]} existingBlocks - An array of existing blocks with their coordinates and sizes.
   * @returns {Coordinates} - The coordinates of the available position for the new block.
   */
  public static findAvailablePosition(
    plateHeight: number,
    newBlock: Size,
    existingBlocks: Coordinates[]
  ): Coordinates {
    const placedBlock: Coordinates = { ...newBlock, top: 0, left: 0 };

    for (let top = 0; top <= plateHeight - placedBlock.height; top += BlockPlacer.ONE_GRID_UNIT) {
      for (let left = 0; left <= DEFAULT_PAGE_WIDTH - placedBlock.width; left += BlockPlacer.ONE_GRID_UNIT) {
        let overlapFound = false;

        placedBlock.top = top;
        placedBlock.left = left;

        for (const existingBlock of existingBlocks) {
          if (BlockPlacer.isOverlapping(placedBlock, existingBlock, BlockPlacer.BUFFER)) {
            overlapFound = true;
            break;
          }
        }

        if (!overlapFound) {
          return placedBlock;
        }
      }
    }
    return { ...placedBlock, top: 0, left: 0 };
  }
}
